// src/components/StudentVideoTestimonialsSection.jsx

import { useState } from "react";
import { motion } from "framer-motion";

import VideoTestimonialCard from "./VideoTestimonialCard";
import videoTestimonials from "../data/videoTestimonials";

const StudentVideoTestimonialsSection = () => {
  const [showAllVideoTestimonials, setShowAllVideoTestimonials] =
    useState(false);

  return (
    <section
      aria-labelledby="video-testimonials-heading"
      className="
        relative
        overflow-hidden
        bg-gray-50/70
        py-10
        dark:bg-gray-950
        sm:py-12
        md:py-14
      "
    >
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          left-1/2
          top-0
          h-80
          w-80
          -translate-x-1/2
          rounded-full
          bg-indigo-500/[0.045]
          blur-3xl
          dark:bg-indigo-500/[0.08]
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          -left-24
          bottom-0
          h-64
          w-64
          rounded-full
          bg-purple-500/[0.025]
          blur-3xl
          dark:bg-purple-500/[0.05]
        "
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6">

        {/* =========================
            HEADER
        ========================= */}
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{
            once: true,
            margin: "0px 0px -45px 0px",
          }}
          transition={{
            duration: 0.45,
            ease: "easeOut",
          }}
          className="
            mx-auto
            mb-7
            max-w-3xl
            text-center
            sm:mb-8
          "
        >
          {/* Eyebrow */}
          <div
            className="
              mb-2.5
              inline-flex
              items-center
              gap-2
              rounded-full
              border
              border-gray-200
              bg-white/80
              px-3
              py-1.5
              text-[10px]
              font-bold
              uppercase
              tracking-[0.15em]
              text-gray-600
              shadow-sm
              backdrop-blur-sm
              dark:border-gray-800
              dark:bg-gray-900/80
              dark:text-gray-300
            "
          >
            <span
              aria-hidden="true"
              className="
                h-1.5
                w-1.5
                rounded-full
                bg-gradient-to-r
                from-indigo-500
                to-purple-500
              "
            />

            Student Experiences
          </div>

          {/* Heading */}
          <h2
            id="video-testimonials-heading"
            className="
              text-[1.8rem]
              font-extrabold
              leading-[1.12]
              tracking-[-0.035em]
              text-gray-950
              dark:text-white
              sm:text-3xl
              md:text-[2.35rem]
            "
          >
            See What Learners{" "}
            <span
              className="
                bg-gradient-to-r
                from-indigo-600
                via-purple-600
                to-pink-500
                bg-clip-text
                text-transparent
              "
            >
              Say About Their Experience
            </span>
          </h2>

          {/* Accent */}
          <div
            aria-hidden="true"
            className="
              mx-auto
              mt-3
              h-[3px]
              w-10
              rounded-full
              bg-gradient-to-r
              from-indigo-600
              to-purple-500
            "
          />

          {/* Description */}
          <p
            className="
              mx-auto
              mt-3
              max-w-2xl
              text-[13px]
              leading-6
              text-gray-600
              dark:text-gray-300
              sm:text-sm
            "
          >
            Watch experiences shared by learners who have trained with
            IA4Solutions and explore what their learning journey looked like.
          </p>
        </motion.header>

        {/* =========================
            VIDEO SHOWCASE
        ========================= */}
        <div className="relative">

          {/* Featured content frame */}
          <div
            className="
              relative
              rounded-[26px]
              border
              border-gray-200/80
              bg-white/70
              p-3
              shadow-[0_14px_45px_rgba(15,23,42,0.06)]
              backdrop-blur-sm
              dark:border-gray-800
              dark:bg-gray-900/60
              dark:shadow-[0_14px_45px_rgba(0,0,0,0.22)]
              sm:p-4
            "
          >
            <div
              className="
                grid
                grid-cols-1
                gap-4
                sm:grid-cols-2
                lg:grid-cols-3
              "
            >
              {(showAllVideoTestimonials
                ? videoTestimonials
                : videoTestimonials.filter(
                    (video) => video.featured
                  )
              ).map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{
                    opacity: 0,
                    y: 14,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    margin: "0px 0px -30px 0px",
                  }}
                  transition={{
                    delay: index * 0.06,
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                  className="h-full"
                >
                  <VideoTestimonialCard
                    id={video.id}
                    title={video.title}
                  />
                </motion.div>
              ))}
            </div>
          </div>

          {/* =========================
              VIEW MORE
          ========================= */}
          {videoTestimonials.length > 3 && (
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={() =>
                  setShowAllVideoTestimonials(
                    (prev) => !prev
                  )
                }
                aria-expanded={
                  showAllVideoTestimonials
                }
                className="
                  group
                  inline-flex
                  min-h-10
                  items-center
                  gap-3
                  rounded-xl
                  border
                  border-gray-200
                  bg-white
                  px-4
                  py-2.5
                  text-xs
                  font-bold
                  text-gray-800
                  shadow-sm
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:border-indigo-200
                  hover:text-indigo-700
                  hover:shadow-md
                  focus:outline-none
                  focus-visible:ring-2
                  focus-visible:ring-indigo-500
                  focus-visible:ring-offset-2
                  dark:border-gray-700
                  dark:bg-gray-900
                  dark:text-gray-100
                  dark:hover:border-indigo-800
                  dark:hover:text-indigo-300
                  dark:focus-visible:ring-offset-gray-950
                "
              >
                <span>
                  {showAllVideoTestimonials
                    ? "Show Featured Experiences"
                    : "View All Student Experiences"}
                </span>

                <span
                  aria-hidden="true"
                  className="
                    flex
                    h-6
                    w-6
                    items-center
                    justify-center
                    rounded-full
                    bg-gray-100
                    text-gray-500
                    transition-all
                    duration-300
                    group-hover:translate-y-0.5
                    group-hover:bg-indigo-100
                    group-hover:text-indigo-600
                    dark:bg-gray-800
                    dark:text-gray-400
                    dark:group-hover:bg-indigo-950/60
                    dark:group-hover:text-indigo-300
                  "
                >
                  {showAllVideoTestimonials
                    ? "↑"
                    : "↓"}
                </span>
              </button>
            </div>
          )}
        </div>

        {/* =========================
            TRUST STATEMENT
        ========================= */}
        <div
          className="
            mx-auto
            mt-6
            flex
            max-w-3xl
            flex-wrap
            items-center
            justify-center
            gap-x-5
            gap-y-2
            text-center
          "
        >
          <span
            className="
              text-[10px]
              font-semibold
              text-gray-500
              dark:text-gray-400
            "
          >
            Learner Experiences
          </span>

          <span
            aria-hidden="true"
            className="
              hidden
              h-3
              w-px
              bg-gray-200
              dark:bg-gray-800
              sm:block
            "
          />

          <span
            className="
              text-[10px]
              font-semibold
              text-gray-500
              dark:text-gray-400
            "
          >
            Practical Learning
          </span>

          <span
            aria-hidden="true"
            className="
              hidden
              h-3
              w-px
              bg-gray-200
              dark:bg-gray-800
              sm:block
            "
          />

          <span
            className="
              text-[10px]
              font-semibold
              text-gray-500
              dark:text-gray-400
            "
          >
            Career-Focused Training
          </span>
        </div>
      </div>
    </section>
  );
};

export default StudentVideoTestimonialsSection;